import { useUpdateWebUser } from "@/hooks/auth/useUpdateWebUser";
import { useHandleError } from "@/hooks/useHandleError";
import { useAuthStore } from "@/store/auth.store";
import { useJoinWithReferralCode } from "@roman/shared/hooks/useJoinWithReferralCode";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../components/Buttons/PrimaryButton";
import { ExclamationMarkIcon } from "../components/Icons/ExclamationMarkIcon";
import { useInappropriateWordsCheck } from "../hooks/useInappropriateWords";

export const EditProfile = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const handleReferralCodeError = useHandleError("紹介コードの使用に失敗しました");
  const fetchUser = useAuthStore((state) => state.fetchUser);

  const [username, setUsername] = useState("");
  const [isLengthError, setIsLengthError] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const { updateWebUser } = useUpdateWebUser();
  const { trigger: joinWithReferralCode } = useJoinWithReferralCode(handleReferralCodeError);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReferralCodeChange = (code: string) => {
    setReferralCode(code);
  };

  const {
    isInappropriate: isInappropriateError,
    checkText: checkInappropriateWords,
    resetCheck: resetInappropriateCheck,
  } = useInappropriateWordsCheck();

  const isButtonDisabled = !username || isLengthError || isInappropriateError || isSubmitting;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setIsLengthError(value.length > 15);
    resetInappropriateCheck();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const { isInappropriate } = checkInappropriateWords(username);
    if (isInappropriate) {
      return;
    }
    setIsSubmitting(true);
    await updateWebUser(
      { name: username },
      {
        onSuccess: async () => {
          await fetchUser();
          if (referralCode) {
            await joinWithReferralCode({ referralCode });
          }
          setIsSubmitting(false);
          navigate("/");
        },
        onError: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  useEffect(() => {
    if (user) setUsername(user.name);
  }, [user]);

  return (
    <div
      className="flex flex-col items-center min-h-[calc(100vh-72px)]"
      style={{
        backgroundImage: `url('/assets/bg.svg')`,
        backgroundRepeat: "repeat",
        backgroundColor: "#f5f5f5",
      }}
    >
      <div className="w-full min-h-[calc(100vh-72px)] max-w-3xl p-8 bg-white">
        <div className="flex flex-col max-w-lg mx-auto gap-10">
          <h1 className="text-2xl font-semibold text-center text-black">アカウント情報登録</h1>

          <div className="flex items-center gap-4 justify-center">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center">
              {user?.photoUrl ? (
                <img src={user.photoUrl} alt="User Icon" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <img src="/assets/usericon.svg" alt="User Icon" className="w-16 h-16" />
              )}
            </div>
          </div>

          <div className="mx-auto flex flex-col gap-8 w-full">
            <div>
              <div className="flex items-start mb-2">
                <div className="flex items-center">
                  <span className="text-lg text-dark-gray">ユーザー名</span>
                  <span className="ml-2 px-2 text-white text-sm bg-red rounded">必須</span>
                </div>
              </div>
              <input
                type="text"
                placeholder="15文字以内"
                className={`w-full p-3 border rounded-lg focus:outline-none ${
                  isLengthError || isInappropriateError
                    ? "border-red focus:border-red"
                    : "border-input focus:border-primary"
                }`}
                value={username}
                onChange={handleUsernameChange}
                maxLength={15}
              />
              <p className="font-normal text-gray01 mt-2 whitespace-nowrap text-xs sm:text-base">※15文字以内</p>
              {isLengthError && (
                <div className="flex items-center gap-1 mt-1">
                  <ExclamationMarkIcon />
                  <p className="text-sm text-red">15文字を超えてます。</p>
                </div>
              )}
              {isInappropriateError && (
                <div className="flex items-center gap-1 mt-1">
                  <ExclamationMarkIcon />
                  <p className="text-sm text-red">その言葉は不適切なため、使用できません。</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-10">
              {/* 初期リリースでは招待コードの入力がないのでコメントアウト */}
              <div>
                <div className="flex items-start mb-2">
                  <div className="flex items-center">
                    <span className="text-lg text-dark-gray">招待コード</span>
                    <span className="ml-2 px-2 text-white text-sm bg-muted rounded">任意</span>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="招待コードを入力"
                  value={referralCode}
                  onChange={(e) => handleReferralCodeChange(e.target.value)}
                  className="w-full p-3 border border-input rounded-lg focus:outline-none focus:border-primary"
                />
                <p className="font-normal text-gray01 mt-2 whitespace-nowrap text-xs sm:text-base">
                  ※招待コードは新規登録後24時間以内に入力する必要があります
                </p>
              </div>
              <PrimaryButton text="登録する" onClick={handleSubmit} disabled={isButtonDisabled} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
